import { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import axios from "axios";
import {
  getToken,
  logErrors,
  post,
  postApi,
  urlConfig,
} from "../../api/getUser";
import { auth } from "../../api/firebase";

export const useRepairReason = () => {
  const { t } = useTranslation("common");
  const { userId, orgId } = useSelector((state) => state.user);

  const [allRepairReasons, setAllRepairReasons] = useState([]);
  const [showRepairReasonPopup, setShowRepairReasonPopup] = useState(false);
  const [tempRepairReasonData, setTempRepairReasonData] = useState(null);
  const [repairReasonPopupErr, setRepairReasonPopupErr] = useState("");
  const [isRepairReasonSaving, setIsRepairReasonSaving] = useState(false);
  const [isRepairReasonDeleting, setIsRepairReasonDeleting] = useState(false);

  const fetchReasons = useCallback(async () => {
    try {
      const response = await postApi(
        {
          querystring: "fetch_all_repair_reasons",
        },
        "workOrder",
      );
      const raw = response?.success && response?.repair_reasons;
      const reasons = Array.isArray(raw)
        ? raw.map((r) => ({ label: r.description, value: r.repair_reason_id }))
        : [];
      setAllRepairReasons(reasons);
    } catch (err) {
      console.log("Error fetching repair reasons:", err);
    }
  }, []);

  useEffect(() => {
    fetchReasons();
  }, [fetchReasons]);

  const handleCreateRepairReason = (data) => {
    const tempData = {
      label: data,
      value: `${orgId}_${Date.now()}`,
      __isNew__: true,
    };
    setTempRepairReasonData(tempData);
    setShowRepairReasonPopup(true);
  };

  const handleSaveRepairReason = async ({
    reasonName,
    reasonId,
    isNew,
    setValue,
    watch,
  }) => {
    setIsRepairReasonSaving(true);
    setRepairReasonPopupErr("");

    try {
      const apiJson = {
        querystring: "addEditRepairReason",
        description: reasonName,
        //action: isNew ? 1 : 2,
        operation: isNew ? "create" : "update",
        repair_reason_id: reasonId,
      };
      const response = await postApi(apiJson, "workOrder");

      if (!response?.success) {
        let errMsg = t("form_err_msg");
        if (response?.repair_reason_data === "duplicate_description") {
          errMsg = t("dup_repair_reason_err");
        }
        // if (response?.repair_reason_data === "description_too_long") {
        //   errMsg = t("repair_reason_too_long_err");
        // }
        setRepairReasonPopupErr(errMsg);
        return;
      }

      if (response?.success) {
        if (isNew) {
          setValue("repairReason", { label: reasonName, value: reasonId });
        } else {
          const currentValue = watch("repairReason");
          if (currentValue?.value === tempRepairReasonData.value) {
            setValue("repairReason", { label: reasonName, value: reasonId });
          }
        }
        await fetchReasons();
        setShowRepairReasonPopup(false);
      }
    } catch (error) {
      console.error("Error saving repair reason:", error);
      setRepairReasonPopupErr(t("form_err_msg"));
    } finally {
      setIsRepairReasonSaving(false);
    }
  };

  const handleDeleteRepairReason = async (reasonId, setValue, watch) => {
    setIsRepairReasonDeleting(true);
    setRepairReasonPopupErr("");

    try {
      const deleteApiData = {
        repair_reason_id: reasonId,
        querystring: "addEditRepairReason",
        action: 3,
      };
      const response = await postApi(deleteApiData, "workOrder");

      if (!response?.success) {
        let errMsg = t("form_err_msg");
        if (response?.delete_result?.length > 0) {
          errMsg = t("delete_repair_reason_err");
        }
        setRepairReasonPopupErr(errMsg);
        return;
      }

      if (response?.success) {
        const currentValue = watch("repairReason");
        if (currentValue?.value === tempRepairReasonData.value) {
          setValue("repairReason", null);
        }
        await fetchReasons();
        setShowRepairReasonPopup(false);
      }
    } catch (error) {
      console.error("Error deleting repair reason:", error);
      setRepairReasonPopupErr(t("form_err_msg"));
    } finally {
      setIsRepairReasonDeleting(false);
    }
  };

  const handleCloseRepairReasonPopup = () => {
    setRepairReasonPopupErr("");
  };

  return {
    allRepairReasons,
    showRepairReasonPopup,
    setShowRepairReasonPopup,
    tempRepairReasonData,
    setTempRepairReasonData,
    repairReasonPopupErr,
    isRepairReasonSaving,
    isRepairReasonDeleting,
    handleCreateRepairReason,
    handleSaveRepairReason,
    handleDeleteRepairReason,
    handleCloseRepairReasonPopup,
  };
};
